import { createReducer, on } from '@ngrx/store';
import {
  AgentGatheringProgress,
  AgentStagedChanges,
  AiChatOption,
  AiChatSelectValue,
  ViewResponse,
  ViewResponseCriteria,
  gateQuestionBelongsInThread,
} from '@app/shared/models/agent-chat.models';
import {
  agentTurnProgressReceived,
  startAgentSessionSuccess,
  startAgentSessionError,
  continueAgentSession,
  continueAgentSessionSuccess,
  continueAgentSessionError,
  loadAgentSessionState,
  loadAgentSessionStateSuccess,
  loadAgentSessionStateError,
  executeStartAgentSession,
  sendAckSuccess,
  sendAckError,
  applyStagedChanges,
  applyStagedChangesError,
  discardStagedChanges,
  stagedChangesError,
  stagedChangesReconciled,
  stageCriteriaEdit,
  reviewAndSearchEmbedded,
  loadChatbotResultsSuccess,
  loadChatbotResultsError,
  agentReplyDelta,
  agentReplyReset,
  agentReplyDone,
  agentTurnProgressLine,
  agentReplySplit,
} from './chatbot.actions';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  fresh?: boolean;
  // This message continues the reply the bubble was typing: it starts from what was
  // already shown instead of from empty.
  continues?: boolean;
  selection?: AiChatSelectValue;
  ack?: string;
}

export interface AgentBarView {
  title: string | null;
  criteria: ViewResponseCriteria[];
  stagedChanges: AgentStagedChanges | null;
}

export interface ChatbotState {
  isLoading: boolean;
  isAckPending: boolean;
  isReviewingAndSearching: boolean;
  threadId: string | null;
  messages: ChatMessage[];
  // How many of the server thread's assistant messages are already in `messages`.
  foldedAssistantCount: number;
  // The gate question already shown for the turn in flight; the harness joins an answered
  // gate's question into the thread, so it comes back as a message we must not repeat.
  shownGateQuestion: string | null;
  response: ViewResponse | null;
  barView: AgentBarView;
  gatheringProgress: AgentGatheringProgress | null;
  draftCriteria: ViewResponseCriteria[] | null;
  isStagedWritePending: boolean;
  isApplyingStagedChanges: boolean;
  // Mid-turn narration from the agent: every line this turn has reported so far, oldest
  // first, transient and never part of `messages`.
  progressLog: string[];
  progressHolds: string[];
  // Where the under-bubble narration starts in `progressLog` (snapshotted when the reply
  // settles or is withdrawn); -1 while the reply is still streaming.
  progressTailFrom: number;
  streamingReply: string;
  streamingReplyComplete: boolean;
  replyStreamOpened: boolean;
  // The ask the stream moved to the gate panel: it outlives `done` so the panel can tell
  // text the recruiter watched from text it has never seen.
  streamedAsk: string | null;
}

const emptyBarView: AgentBarView = { title: null, criteria: [], stagedChanges: null };
// GET /state answers null for a thread that never checkpointed (a turn that dropped before
// its first write), so every reader of a loaded response tolerates null.
const barViewFrom = (response: ViewResponse | null): AgentBarView => ({
  title: response?.title ?? null,
  criteria: response?.criteria ?? [],
  stagedChanges: response?.staged ?? null,
});

const gatheringProgressFrom = (response: ViewResponse | null): AgentGatheringProgress | null =>
  response?.gathering_progress ?? null;

const draftCriteriaFrom = (response: ViewResponse | null): ViewResponseCriteria[] | null =>
  response?.staged?.criteria_preview ??
  (response?.gate?.kind === 'feedback_review' ? (response.gate.criteria ?? null) : null);

const mapLoadedMessages = (rawMessages: ViewResponse['messages']): ChatMessage[] =>
  rawMessages.map((message, index) => {
    const content = message.content || '';
    const previousMessage = rawMessages[index - 1];
    const selection = getLoadedSelection(content, previousMessage?.role === 'assistant');

    return {
      role: message.role,
      content,
      ...(selection ? { selection } : {}),
    };
  });

const getLoadedSelection = (
  content: string,
  followsAssistantMessage: boolean,
): AiChatSelectValue | null => {
  if (!followsAssistantMessage) {
    return null;
  }

  const trimmedContent = content.trim();

  if (!trimmedContent) {
    return null;
  }

  const customValueSeparatorIndex = trimmedContent.indexOf(';');
  const hasCustomValueSeparator = customValueSeparatorIndex !== -1;
  const selectedContent = hasCustomValueSeparator
    ? trimmedContent.slice(0, customValueSeparatorIndex)
    : trimmedContent;
  const customValue = hasCustomValueSeparator
    ? trimmedContent.slice(customValueSeparatorIndex + 1).trim()
    : '';
  const selectedOptions = selectedContent
    .split(/\r?\n|,/)
    .map((label) => label.trim())
    .filter(Boolean)
    .map<AiChatOption>((label, index) => ({ id: index, label }));

  if (selectedOptions.length > 1 || (selectedOptions.length > 0 && customValue)) {
    return {
      selectedOptions,
      ...(customValue ? { customValue } : {}),
    };
  }

  return null;
};

const getAssistantMessages = (response: ViewResponse | null): string[] =>
  (response?.messages ?? [])
    .filter((message) => message.role === 'assistant')
    .map((message) => message.content);

const getTrailingAssistantMessage = (response: ViewResponse | null): string | undefined =>
  getAssistantMessages(response).at(-1);

const getPendingAssistantQuestion = (response: ViewResponse | null): string | undefined => {
  if (response?.gate?.question) {
    return response.gate.question;
  }

  if (response?.gate === null) {
    return getTrailingAssistantMessage(response);
  }

  return undefined;
};

// The harness splits a streamed reply at commit -- lead-in to the thread, question to the
// gate -- and lightly rewrites punctuation, so match on normalised text, not equality.
const normalise = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Whole replies often end with the gate question, so "already shown" means ends with it.
const endsWithText = (message: string | undefined, text: string): boolean => {
  const whole = normalise(message ?? '');
  const tail = normalise(text);
  return !!tail && (whole === tail || whole.endsWith(` ${tail}`));
};

// A parked gate MAY carry new assistant messages of its own (the guided review and
// comparison briefs); the gate question prompts for them and never replaces them.
const withGateMessages = (
  state: ChatbotState,
  response: ViewResponse,
): Pick<ChatbotState, 'messages' | 'foldedAssistantCount' | 'shownGateQuestion'> => {
  const all = getAssistantMessages(response);
  const settled = { foldedAssistantCount: all.length, shownGateQuestion: null };
  const streamed = normalise(state.streamingReply);
  // The committed text can be SHORTER than the stream (the ask moved to the gate) or LONGER
  // (a finalize lead-in gains a sentence); either way one message continues what was shown.
  let continued = false;
  const asMessage = (content: string): ChatMessage => {
    const candidate = normalise(content);
    const continues =
      !continued &&
      !!candidate &&
      !!streamed &&
      (candidate.startsWith(streamed) || streamed.startsWith(candidate));
    continued = continued || continues;
    // `fresh` means "animate what has not been shown yet": the seed decides how much of that
    // is left, while text the stream covered without continuing it just settles.
    const fresh = continues || !streamed || !streamed.startsWith(candidate);
    return { role: 'assistant', content, fresh, ...(continues ? { continues } : {}) };
  };

  if (!response.gate?.question) {
    // With no gate at all the trailing message IS the turn's reply and nothing else renders
    // it, so it stays in the thread; a gate widget re-renders its own prompt, so that one goes.
    const upTo = response.gate ? -1 : undefined;
    const fresh = all
      .slice(state.foldedAssistantCount, upTo)
      .filter((content) => content && content !== state.shownGateQuestion)
      .map(asMessage);
    return { ...settled, messages: fresh.length ? [...state.messages, ...fresh] : state.messages };
  }

  // Fold by POSITION, not by content: the same line can legitimately recur in a thread,
  // and a gate can park with no new message of its own at all.
  const isGateQuestion = (content: string) =>
    content === response.gate?.question && (!streamed || normalise(content) !== streamed);
  const fresh = all
    .slice(state.foldedAssistantCount)
    .filter((content) => content && !isGateQuestion(content) && content !== state.shownGateQuestion)
    .map(asMessage);

  // An option-less gate other than open_question renders only its input and actions, so its
  // question joins the thread, and shownGateQuestion stops the harness re-adding it.
  if (gateQuestionBelongsInThread(response.gate)) {
    const question = response.gate?.question ?? '';
    const lastShown = fresh.length ? fresh[fresh.length - 1] : state.messages.at(-1);
    const alreadyThere =
      fresh.some((message) => message.content === question) ||
      endsWithText(lastShown?.content, question) ||
      state.messages.at(-1)?.content === question;
    const withQuestion = alreadyThere ? fresh : [...fresh, asMessage(question)];
    return {
      ...settled,
      shownGateQuestion: question,
      messages: withQuestion.length ? [...state.messages, ...withQuestion] : state.messages,
    };
  }

  return { ...settled, messages: fresh.length ? [...state.messages, ...fresh] : state.messages };
};

const withAck = (state: ChatbotState, ack: string): ChatbotState => {
  if (!state.isAckPending) {
    return state;
  }
  const messages = [...state.messages];
  const lastMessage = messages[messages.length - 1];
  if (lastMessage && lastMessage.role === 'user') {
    messages[messages.length - 1] = { ...lastMessage, ack };
  }
  return { ...state, isAckPending: false, messages };
};

export const initialState: ChatbotState = {
  isLoading: false,
  isAckPending: false,
  isReviewingAndSearching: false,
  threadId: null,
  messages: [],
  foldedAssistantCount: 0,
  shownGateQuestion: null,
  response: null,
  barView: emptyBarView,
  gatheringProgress: null,
  draftCriteria: null,
  isStagedWritePending: false,
  isApplyingStagedChanges: false,
  progressLog: [],
  progressHolds: [],
  progressTailFrom: -1,
  streamingReply: '',
  streamingReplyComplete: false,
  replyStreamOpened: false,
  streamedAsk: null,
};

export const chatbotReducer = createReducer(
  initialState,
  on(executeStartAgentSession, (state, { payload }) => ({
    ...state,
    isLoading: true,
    isAckPending: true,
    threadId: payload.threadId,
    messages: [{ role: 'user' as const, content: payload.prompt }],
    foldedAssistantCount: 0,
    shownGateQuestion: null,
    response: null,
    gatheringProgress: null,
    draftCriteria: null,
    isStagedWritePending: false,
    isApplyingStagedChanges: false,
  })),
  on(startAgentSessionSuccess, (state, { payload }) => ({
    ...state,
    isLoading: false,
    isAckPending: false,
    barView: barViewFrom(payload.response),
    gatheringProgress: gatheringProgressFrom(payload.response),
    draftCriteria: draftCriteriaFrom(payload.response),
    ...withGateMessages(state, payload.response),
    response: payload.response,
  })),
  on(startAgentSessionError, (state) => ({
    ...state,
    isLoading: false,
    isAckPending: false,
  })),
  on(continueAgentSession, (state, { payload }) => {
    const previousQuestion = getPendingAssistantQuestion(state.response);
    const newMessages = state.messages.map((message) =>
      message.fresh ? { ...message, fresh: false } : message,
    );

    // The reply above usually ends with the question already, so only a separate ask is added.
    if (previousQuestion && !endsWithText(newMessages.at(-1)?.content, previousQuestion)) {
      newMessages.push({ role: 'assistant', content: previousQuestion });
    }

    if (payload.prompt || payload.selection) {
      const shouldShowSelectionSummary =
        payload.selection && payload.showSelectionSummary !== false;

      newMessages.push({
        role: 'user',
        content: payload.prompt || '',
        ...(shouldShowSelectionSummary ? { selection: payload.selection } : {}),
      });
    }

    return {
      ...state,
      isLoading: true,
      isAckPending: !!payload.prompt,
      messages: newMessages,
      shownGateQuestion: previousQuestion ?? null,
      response: null,
    };
  }),
  on(continueAgentSessionSuccess, (state, { payload }) => ({
    ...state,
    isLoading: false,
    isAckPending: false,
    barView: barViewFrom(payload.response),
    gatheringProgress: gatheringProgressFrom(payload.response),
    draftCriteria: draftCriteriaFrom(payload.response),
    ...withGateMessages(state, payload.response),
    response: payload.response,
  })),
  on(continueAgentSessionError, (state) => ({
    ...state,
    isLoading: false,
    isAckPending: false,
  })),
  on(loadAgentSessionState, (state, { payload }) => ({
    ...state,
    isLoading: true,
    isAckPending: false,
    threadId: payload.threadId,
    draftCriteria: null,
    isStagedWritePending: false,
    isApplyingStagedChanges: false,
  })),
  on(loadAgentSessionStateSuccess, (state, { payload }) => {
    // A null envelope means the thread has no checkpoint to render: end the wait and keep
    // whatever is on screen rather than blanking a live thread.
    if (!payload.response) {
      return { ...state, isLoading: false };
    }

    const rawMessages = payload.response?.messages || [];
    // An answered gate's question is folded in after a reply that may already end with it.
    const mappedMessages = mapLoadedMessages(rawMessages).filter(
      (message, index, all) =>
        message.role !== 'assistant' ||
        all[index - 1]?.role !== 'assistant' ||
        !endsWithText(all[index - 1].content, message.content),
    );

    // Pop only what a gate widget re-renders as its own prompt, a panel's or an open question's:
    // a gate-less reply and any other option-less gate's question stay in the thread.
    const gate = payload.response.gate;
    const questionInThread = gateQuestionBelongsInThread(gate);
    const trailing = mappedMessages[mappedMessages.length - 1];
    const widgetRendersTrailing =
      !!gate && !questionInThread && (!gate.question || trailing?.content === gate.question);

    if (trailing?.role === 'assistant' && widgetRendersTrailing) {
      mappedMessages.pop();
    }

    // A parked option-less gate other than open_question keeps its question out of the saved
    // thread until answered, so a reload puts it back as settled text or it is nowhere on screen.
    const threadQuestion = questionInThread ? (gate?.question ?? '') : '';
    if (threadQuestion && !endsWithText(mappedMessages.at(-1)?.content, threadQuestion)) {
      mappedMessages.push({ role: 'assistant', content: threadQuestion, fresh: false });
    }

    return {
      ...state,
      isLoading: false,
      barView: barViewFrom(payload.response),
      gatheringProgress: gatheringProgressFrom(payload.response),
      draftCriteria: draftCriteriaFrom(payload.response),
      messages: mappedMessages,
      foldedAssistantCount: getAssistantMessages(payload.response).length,
      shownGateQuestion: threadQuestion || null,
      response: payload.response,
    };
  }),
  on(loadAgentSessionStateError, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(sendAckSuccess, (state, { payload }) => withAck(state, payload.ack || 'Got it.')),
  on(sendAckError, (state) => withAck(state, 'Got it.')),
  on(stageCriteriaEdit, (state, { payload }) => ({
    ...state,
    draftCriteria: payload.criteria,
    isStagedWritePending: true,
  })),
  on(discardStagedChanges, (state) => ({
    ...state,
    isStagedWritePending: true,
  })),
  on(stagedChangesReconciled, (state, { payload }) => ({
    ...state,
    response: payload.response,
    barView: barViewFrom(payload.response),
    gatheringProgress: gatheringProgressFrom(payload.response),
    draftCriteria: draftCriteriaFrom(payload.response),
    isStagedWritePending: false,
  })),
  on(stagedChangesError, (state) => ({
    ...state,
    draftCriteria: state.response ? draftCriteriaFrom(state.response) : null,
    isStagedWritePending: false,
  })),
  on(applyStagedChanges, (state) => ({
    ...state,
    isApplyingStagedChanges: true,
  })),
  on(applyStagedChangesError, (state) => ({
    ...state,
    isApplyingStagedChanges: false,
  })),
  on(loadChatbotResultsSuccess, loadChatbotResultsError, (state) => ({
    ...state,
    isApplyingStagedChanges: false,
  })),
  on(reviewAndSearchEmbedded, (state) => ({
    ...state,
    isReviewingAndSearching: true,
  })),
  on(loadChatbotResultsSuccess, continueAgentSessionError, (state) => ({
    ...state,
    isReviewingAndSearching: false,
  })),
  on(agentTurnProgressReceived, (state, { payload }) => ({
    ...state,
    progressLog: payload.log,
    progressHolds: payload.holds,
  })),
  // The narration belongs to the turn in flight: any turn boundary or thread reset clears it.
  on(
    executeStartAgentSession,
    continueAgentSession,
    loadAgentSessionState,
    startAgentSessionSuccess,
    startAgentSessionError,
    continueAgentSessionSuccess,
    continueAgentSessionError,
    applyStagedChangesError,
    (state) => ({
      ...state,
      progressLog: [],
      progressHolds: [],
      progressTailFrom: -1,
      streamingReply: '',
      streamingReplyComplete: false,
      replyStreamOpened: false,
    }),
  ),
  // The streamed ask has to survive `done` to reach the gate panel, so only the next turn
  // clears it -- every start, including the blocking ones that never stream.
  on(
    executeStartAgentSession,
    continueAgentSession,
    loadAgentSessionState,
    applyStagedChanges,
    reviewAndSearchEmbedded,
    (state) => ({
      ...state,
      streamedAsk: null,
    }),
  ),
  on(agentReplyDelta, (state, { payload }) => ({
    ...state,
    streamingReply: state.streamingReply + payload.text,
    replyStreamOpened: true,
  })),
  // A withdrawn attempt un-types itself: the bubble stays mounted and the target goes empty.
  // The retry line lands next, so the narration below the bubble starts here.
  on(agentReplyReset, (state) => ({
    ...state,
    streamingReply: '',
    streamingReplyComplete: false,
    streamedAsk: null,
    progressTailFrom: state.progressLog.length,
  })),
  // Anything narrated from here on is the structured tail, not the decide: it renders below
  // the bubble, and the lines that preceded the reply must not repeat there.
  on(agentReplyDone, (state) => ({
    ...state,
    streamingReplyComplete: true,
    progressTailFrom: state.progressLog.length,
  })),
  // The ask is moving to the gate panel: drop it from the thread now, not silently at commit.
  on(agentReplySplit, (state, { payload }) => ({
    ...state,
    streamingReply: payload.leadIn,
    streamedAsk: payload.ask,
  })),
  on(agentTurnProgressLine, (state, { payload }) => ({
    ...state,
    progressLog:
      state.progressLog[state.progressLog.length - 1] === payload.text
        ? state.progressLog
        : [...state.progressLog, payload.text],
    progressHolds: payload.holds,
  })),
);
